import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

/** ********************************************************************************************************************
 * ITEM MODIFIERS
 ******************************************************************************************************************** */

export function useItemModifierMutations() {
  const app = useRealmApp();
  return {
    addItemModifier: useAddItemModifier(app.currentUser.id),
    updateItemModifier: useUpdateItemModifier(app.currentUser.id),
    deleteItemModifier: useDeleteItemModifier(app.currentUser.id),
  };
}

const AddItemModifierMutation = gql`
  mutation AddItemModifier($itemModifier: ItemModifierInsertInput!) {
    addedItemModifier: insertOneItemModifier(data: $itemModifier) {
      _id
      name
      modifiers {
        _id
        isQuantifiable
        name
        price
        toAdd
        toRemove
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateItemModifierMutation = gql`
  mutation UpdateItemModifier(
    $itemModifierId: ObjectId!
    $ownerId: String!
    $updates: ItemModifierUpdateInput!
  ) {
    updatedItemModifier: updateOneItemModifier(
      query: { _id: $itemModifierId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      name
      modifiers {
        _id
        isQuantifiable
        name
        price
        toAdd
        toRemove
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteItemModifierMutation = gql`
  mutation DeleteItemModifier($itemModifierId: ObjectId!, $ownerId: String!) {
    deletedItemModifier: deleteOneItemModifier(
      query: { _id: $itemModifierId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const ItemModifierFieldsFragment = gql`
  fragment ItemModifierFields on ItemModifier {
    _id
    name
    modifiers {
      _id
      isQuantifiable
      name
      price
      toAdd
      toRemove
    }
    updateDatetime
    creationDatetime
  }
`;

function useAddItemModifier(ownerId) {
  const [addItemModifierMutation] = useMutation(AddItemModifierMutation, {
    // Manually save added ItemModifiers into the Apollo cache so that ItemModifier queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedItemModifier } }) => {
      cache.modify({
        fields: {
          itemModifiers: (existingItemModifiers = []) => [
            ...existingItemModifiers,
            cache.writeFragment({
              data: addedItemModifier,
              fragment: ItemModifierFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (itemModifier) => {
    //
    const toAdd = {
      owner_id: ownerId,
      ...itemModifier,
    };
    toAdd.modifiers?.forEach((modifier) => {
      modifier.owner_id = ownerId;
    });
    //
    const {
      data: { addedItemModifier },
    } = await addItemModifierMutation({
      variables: {
        itemModifier: toAdd,
      },
    });
    return addedItemModifier;
  };
}

function useUpdateItemModifier(ownerId) {
  const [updateItemModifierMutation] = useMutation(UpdateItemModifierMutation);
  return async (itemModifier, updates) => {
    //
    updates.modifiers?.forEach((modifier) => {
      modifier.owner_id = ownerId;
    });
    //
    const {
      data: { updatedItemModifier },
    } = await updateItemModifierMutation({
      variables: { itemModifierId: itemModifier._id, ownerId, updates },
    });
    return updatedItemModifier;
  };
}

function useDeleteItemModifier(ownerId) {
  const [deleteItemModifierMutation] = useMutation(DeleteItemModifierMutation, {
    update: (cache, { data: { deletedItemModifier } }) => {
      cache.modify({
        fields: {
          itemModifiers: (existingItemModifiers = [], { readField }) =>
            existingItemModifiers.filter(
              (ref) => deletedItemModifier._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (itemModifier) => {
    const {
      data: { deletedItemModifier },
    } = await deleteItemModifierMutation({
      variables: { itemModifierId: itemModifier._id, ownerId },
    });
    return deletedItemModifier;
  };
}

/** ********************************************************************************************************************
 * END ITEM MODIFIERS
 ******************************************************************************************************************** */

/** ********************************************************************************************************************
 * ITEM VARIANTS
 ******************************************************************************************************************** */

export function useItemVariantMutations() {
  const app = useRealmApp();
  return {
    addItemVariant: useAddItemVariant(app.currentUser.id),
    updateItemVariant: useUpdateItemVariant(app.currentUser.id),
    deleteItemVariant: useDeleteItemVariant(app.currentUser.id),
  };
}

const AddItemVariantMutation = gql`
  mutation AddItemVariant($itemVariant: ItemVariantInsertInput!) {
    addedItemVariant: insertOneItemVariant(data: $itemVariant) {
      _id
      name
      variants {
        _id
        name
        color
        size
        items {
          name
          value
        }
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateItemVariantMutation = gql`
  mutation UpdateItemVariant(
    $itemVariantId: ObjectId!
    $ownerId: String!
    $updates: ItemVariantUpdateInput!
  ) {
    updatedItemVariant: updateOneItemVariant(
      query: { _id: $itemVariantId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      name
      variants {
        _id
        name
        color
        size
        items {
          name
          value
        }
      }
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteItemVariantMutation = gql`
  mutation DeleteItemVariant($itemVariantId: ObjectId!, $ownerId: String!) {
    deletedItemVariant: deleteOneItemVariant(
      query: { _id: $itemVariantId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const ItemVariantFieldsFragment = gql`
  fragment ItemVariantFields on ItemVariant {
    _id
    name
    variants {
      _id
      name
      color
      size
      items {
        name
        value
      }
    }
    updateDatetime
    creationDatetime
  }
`;

function useAddItemVariant(ownerId) {
  const [addItemVariantMutation] = useMutation(AddItemVariantMutation, {
    // Manually save added ItemVariants into the Apollo cache so that ItemVariant queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedItemVariant } }) => {
      cache.modify({
        fields: {
          itemVariants: (existingItemVariants = []) => [
            ...existingItemVariants,
            cache.writeFragment({
              data: addedItemVariant,
              fragment: ItemVariantFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (itemVariant) => {
    //
    const toAdd = {
      owner_id: ownerId,
      ...itemVariant,
    };
    toAdd.variants?.forEach((variant) => {
      variant.owner_id = ownerId;
      variant.items?.forEach((item) => {
        item.owner_id = ownerId;
      });
    });
    //
    const {
      data: { addedItemVariant },
    } = await addItemVariantMutation({
      variables: {
        itemVariant: toAdd,
      },
    });
    return addedItemVariant;
  };
}

function useUpdateItemVariant(ownerId) {
  const [updateItemVariantMutation] = useMutation(UpdateItemVariantMutation);
  return async (itemVariant, updates) => {
    //
    updates.variants?.forEach((variant) => {
      variant.owner_id = ownerId;
      variant.items?.forEach((item) => {
        item.owner_id = ownerId;
      });
    });
    //
    const {
      data: { updatedItemVariant },
    } = await updateItemVariantMutation({
      variables: { itemVariantId: itemVariant._id, ownerId, updates },
    });
    return updatedItemVariant;
  };
}

function useDeleteItemVariant(ownerId) {
  const [deleteItemVariantMutation] = useMutation(DeleteItemVariantMutation, {
    update: (cache, { data: { deletedItemVariant } }) => {
      cache.modify({
        fields: {
          itemVariants: (existingItemVariants = [], { readField }) =>
            existingItemVariants.filter(
              (ref) => deletedItemVariant._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (itemVariant) => {
    const {
      data: { deletedItemVariant },
    } = await deleteItemVariantMutation({
      variables: { itemVariantId: itemVariant._id, ownerId },
    });
    return deletedItemVariant;
  };
}

/** ********************************************************************************************************************
 * END ITEM VARIANTS
 ******************************************************************************************************************** */
